import { createContext, useCallback, useContext, useEffect, useRef, useState, lazy, Suspense, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import api, { setCsrfToken, clearCsrfToken } from '@/lib/api';
import { connectSocket, disconnectSocket } from '@/lib/socket';
import { hasUnsavedChanges } from '@/hooks/useUnsavedChangesGuard';
import { onSessionExpired } from '@/lib/sessionExpiredBus';
import type {
  AuthMeResponse,
  LoginRequest,
  LoginResponse,
  UserPreferences,
  UserPublic,
  SubscriptionPlan,
  Tenant,
} from '@amuster/types';

interface AuthState {
  user: UserPublic | null;
  tenant: (Pick<Tenant, 'id' | 'tenant_key' | 'name' | 'branding' | 'settings' | 'subscription_status' | 'subscription_plan_id'> & {
    industry_code: string | null;
    primary_vertical_pack_code: string | null;
  }) | null;
  plan: Pick<SubscriptionPlan, 'plan_code' | 'name' | 'features' | 'max_campuses' | 'max_students' | 'max_staff' | 'max_concurrent_sessions'> | null;
  preferences: UserPreferences | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

/** Returned by login() when MFA is required before full session. */
export interface MfaLoginResult {
  requiresMfa: true;
  mfaPendingToken: string;
  mfaMethods: string[];
}

/** Returned by login() when the user must set up MFA (role enforcement). */
export interface MfaSetupResult {
  requiresMfaSetup: true;
  limitedToken?: string;
}

/** Returned by login() when the user must verify their email first. */
export interface EmailVerificationResult {
  requiresEmailVerification: true;
}

type LoginResult = MfaLoginResult | MfaSetupResult | EmailVerificationResult | void;

interface AuthContextValue extends AuthState {
  /**
   * True when the session was lost and could not be silently refreshed WHILE a
   * tracked form has unsaved edits — the central, dirty-aware session-expired
   * signal (SESSION PR-C2b-A). Low-level transport code never hard-redirects; it
   * routes here so unsaved work is preserved. The visible save/extend UX is
   * PR-C2b-B.
   */
  sessionExpired: boolean;
  /**
   * True from mount until the FIRST session bootstrap fully settles — including
   * any in-flight transport refresh AND the recovered-bootstrap `/auth/me`
   * re-probe (SESSION PR-C2b-B1b). Route guards treat this like `isLoading`: they
   * render the loading state and never redirect while it is true, so a cold tab
   * whose access token was merely expired-but-refreshable can never flash /login
   * before the silent recovery settles.
   */
  isBootstrapping: boolean;
  login: (data: LoginRequest) => Promise<LoginResult>;
  /** Complete login after MFA verification — fetches CSRF + /auth/me. */
  completeMfaLogin: () => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  /**
   * @deprecated With cookie-first auth, tokens are managed via HttpOnly cookies.
   * This method now just triggers a session refresh from the server.
   * For SSO/onboarding flows, call refreshUser() after the API sets cookies.
   */
  setTokens: (accessToken?: string, refreshToken?: string, tenantKey?: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Fetch CSRF token from server and store in memory.
 * Called on app init and after login.
 */
async function fetchCsrfToken(): Promise<void> {
  try {
    const response = await api.get<{ success: boolean; data: { csrf_token: string } }>('/auth/csrf-token');
    setCsrfToken(response.data.data.csrf_token);
  } catch {
    // CSRF fetch failed; state-changing requests will fail until retry
    console.warn('Failed to fetch CSRF token');
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<AuthState>({
    user: null,
    tenant: null,
    plan: null,
    preferences: null,
    isAuthenticated: false,
    isLoading: true,
  });

  // Route-guard gate for the very first bootstrap (incl. the recovery re-probe).
  // Cleared in the bootstrap effect's `finally` — see SESSION PR-C2b-B1b.
  const [isBootstrapping, setIsBootstrapping] = useState(true);

  /**
   * Bootstrap session from server.
   * Cookie-first: server reads access_token from HttpOnly cookie.
   * No localStorage involved.
   */
  const fetchMe = useCallback(async () => {
    // Apply a successful /auth/me payload → authenticated, settled.
    const applyMe = (data: AuthMeResponse): void => {
      const { user, tenant, plan, preferences } = data;
      setState({
        user,
        tenant,
        plan,
        preferences: preferences ?? null,
        isAuthenticated: true,
        isLoading: false,
      });
      // Connect socket after successful auth (uses cookie-based auth)
      connectSocket();
    };

    try {
      const response = await api.get<{ success: boolean; data: AuthMeResponse }>('/auth/me');
      applyMe(response.data.data);
    } catch {
      // Recovered-bootstrap re-probe (SESSION PR-C2b-B1b). The transport
      // interceptor may have silently refreshed the access token while the
      // ORIGINAL /auth/me chain still settled into this catch — the live
      // diagnostic proved a recovered `/auth/me` 200 can fail to propagate back to
      // this bootstrap awaiter, stranding a user with a VALID server session on
      // /login. Before committing unauthenticated, perform EXACTLY ONE bounded
      // `/auth/me` re-probe against the now-possibly-fresh cookies. On its failure
      // we commit unauthenticated exactly as before: a single extra GET, no loop,
      // and the transport layer still owns the dirty-aware session-expired signal.
      try {
        const reprobe = await api.get<{ success: boolean; data: AuthMeResponse }>('/auth/me');
        applyMe(reprobe.data.data);
      } catch {
        // Not authenticated, or the session genuinely expired (re-probe also failed).
        setState({ user: null, tenant: null, plan: null, preferences: null, isAuthenticated: false, isLoading: false });
      }
    }
  }, []);

  // Bootstrap on mount: fetch CSRF token and session state
  useEffect(() => {
    const bootstrap = async () => {
      try {
        await fetchCsrfToken();
        await fetchMe();
      } finally {
        // The first bootstrap — including any in-flight transport refresh and the
        // recovered-bootstrap `/auth/me` re-probe inside fetchMe — has fully
        // settled. Releasing the gate lets the route guards evaluate
        // isAuthenticated; until now they render the loading state and never
        // redirect, so a recovered session can never flash /login (PR-C2b-B1b).
        setIsBootstrapping(false);
      }
    };
    void bootstrap();
  }, [fetchMe]);

  // ── Central session-expired handling (SESSION PR-C2b-A) ─────────────────────
  // The axios interceptor dispatches a session-expired signal (instead of a hard
  // `window.location.href = '/login'`) when a silent refresh is unrecoverable.
  // AuthContext owns the dirty-aware transition.
  const [sessionExpired, setSessionExpired] = useState(false);
  const handlingExpiryRef = useRef(false);

  useEffect(() => {
    const unsubscribe = onSessionExpired(() => {
      // Handle once per expiry — repeated 401s from background pollers must not
      // loop or re-prompt.
      if (handlingExpiryRef.current) return;
      handlingExpiryRef.current = true;

      if (hasUnsavedChanges()) {
        // Dirty: do NOT clear or redirect. Preserve the user's unsaved edits and
        // surface the session-expired state (the visible save/extend UX is
        // PR-C2b-B). This is the core safety guarantee: low-level transport code
        // never destroys unsaved work.
        setSessionExpired(true);
      } else {
        // Clean: graceful LOCAL sign-out — no `/auth/logout` POST (the server
        // session is already gone) and no hard redirect. Clearing CSRF + cache +
        // socket and dropping `isAuthenticated` lets the route guard navigate to
        // /login via the router.
        disconnectSocket();
        clearCsrfToken();
        queryClient.clear();
        setState({ user: null, tenant: null, plan: null, preferences: null, isAuthenticated: false, isLoading: false });
      }
    });
    return unsubscribe;
  }, [queryClient]);

  // Re-authenticating clears the one-shot guard + expired flag so a future
  // expiry is handled fresh. Fires only on isAuthenticated transitions, so it
  // never clobbers the dirty-case `sessionExpired` flag (which keeps
  // isAuthenticated true to preserve the mounted form).
  useEffect(() => {
    if (state.isAuthenticated) {
      handlingExpiryRef.current = false;
      setSessionExpired(false);
    }
  }, [state.isAuthenticated]);

  /**
   * Login with email/password.
   * API sets HttpOnly cookies. We only set isAuthenticated after GET /auth/me
   * succeeds so we never redirect to dashboard then back to login (flash).
   *
   * Returns MFA state if MFA is required; otherwise completes the session.
   */
  const login = useCallback(async (data: LoginRequest): Promise<LoginResult> => {
    const res = await api.post<{ success: boolean; data: LoginResponse }>('/auth/login', data);
    const loginData = res.data.data;

    // MFA required — no session cookies set yet, return pending state
    if (loginData.requiresMfa && loginData.mfaPendingToken) {
      return {
        requiresMfa: true,
        mfaPendingToken: loginData.mfaPendingToken,
        mfaMethods: loginData.mfaMethods ?? ['totp'],
      };
    }

    // MFA setup required for privileged roles — no full session yet
    if (loginData.requiresMfaSetup) {
      return {
        requiresMfaSetup: true,
        limitedToken: loginData.limitedToken,
      };
    }

    // Email verification required — no session cookies set
    if (loginData.requiresEmailVerification) {
      return { requiresEmailVerification: true };
    }

    // Normal login — cookies are set, proceed to session bootstrap
    await fetchCsrfToken();

    const meResponse = await api.get<{ success: boolean; data: AuthMeResponse }>('/auth/me');
    const { user, tenant, plan, preferences } = meResponse.data.data;

    setState({
      user,
      tenant,
      plan,
      preferences: preferences ?? null,
      isAuthenticated: true,
      isLoading: false,
    });

    connectSocket();
  }, []);

  /**
   * Complete login after MFA verification.
   * Backend has set HttpOnly session cookies after successful MFA verify.
   * We just need CSRF + /auth/me to bootstrap frontend state.
   */
  const completeMfaLogin = useCallback(async () => {
    await fetchCsrfToken();

    const meResponse = await api.get<{ success: boolean; data: AuthMeResponse }>('/auth/me');
    const { user, tenant, plan, preferences } = meResponse.data.data;

    setState({
      user,
      tenant,
      plan,
      preferences: preferences ?? null,
      isAuthenticated: true,
      isLoading: false,
    });

    connectSocket();
  }, []);

  /**
   * Logout and clear session.
   * API clears HttpOnly cookies.
   */
  const logout = useCallback(async () => {
    // Dirty-aware logout: an in-app logout does NOT fire `beforeunload`, so if a
    // tracked form (e.g. the Incident editor) has unsaved edits, confirm before
    // discarding. Offers Discard (OK) / Stay (Cancel); save-before-logout is
    // deferred to PR-D2. No business data is mutated here.
    if (hasUnsavedChanges() &&
      !window.confirm('You have unsaved changes that will be lost if you sign out. Discard them and sign out?')) {
      return;
    }
    try {
      await api.post('/auth/logout');
    } catch {
      // Ignore logout errors
    }
    disconnectSocket();
    // Clear the in-memory CSRF token so a stale token from this session can't be
    // reused by a fresh login on the same page (avoids post-login 403 CSRF).
    clearCsrfToken();
    // Clear all cached data to prevent cross-user data leakage
    queryClient.clear();
    setState({ user: null, tenant: null, plan: null, preferences: null, isAuthenticated: false, isLoading: false });
  }, [queryClient]);

  /**
   * Refresh user data from server.
   */
  const refreshUser = useCallback(async () => {
    await fetchMe();
  }, [fetchMe]);

  /**
   * @deprecated Legacy method for SSO/onboarding flows.
   * With cookie-first auth, the API sets cookies directly.
   * This method now just refreshes the session from server.
   */
  const setTokens = useCallback(async (_accessToken?: string, _refreshToken?: string, _tenantKey?: string) => {
    // With cookie-first auth, tokens are set by the server via HttpOnly cookies.
    // Just refresh session state and CSRF token.
    await fetchCsrfToken();
    connectSocket();
    await fetchMe();
  }, [fetchMe]);

  // Lazy-load session lifecycle guard to avoid circular dependency and
  // keep the initial bundle size unchanged when the feature is off.
  const SessionLifecycleGuard = lazy(() =>
    import('@/components/auth/SessionLifecycleGuard').then((m) => ({ default: m.SessionLifecycleGuard }))
  );

  return (
    <AuthContext.Provider value={{ ...state, sessionExpired, isBootstrapping, login, completeMfaLogin, logout, refreshUser, setTokens }}>
      {children}
      {state.isAuthenticated && (
        <Suspense fallback={null}>
          <SessionLifecycleGuard />
        </Suspense>
      )}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
